import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  ArrowRight, Clock, TrendingUp, Calendar, RefreshCw,
  ChevronDown, ChevronUp, MapPin, ArrowUp, ArrowDown, Minus,
  ChevronLeft, ChevronRight, Map, Maximize2, X,
} from 'lucide-react';
import lprJourneyAPI from '../../../../services/lprJourneyAPI';
import imageService from '../../../../services/imageHandlerAPI';
import LprJourneyGhostPreview from './LprJourneyGhostPreview';
import lprJourneyCameraTransitionImg from '../../../../images/lprJourneyCameraTransition.png';
import './CameraTransitions.css';

/* ─── constants ──────────────────────────────────────────────────────────── */

/** Tolerance band (±%) within which the trend arrow is shown as neutral. */
export const TREND_NEUTRAL_TOLERANCE_PCT = 2;

/** Default rolling-window (days) for the historical baseline. */
export const HISTORY_WINDOW_DAYS = 30;

/* ─── helpers ────────────────────────────────────────────────────────────── */

/**
 * Return today's date string (YYYY-MM-DD) in the given IANA timezone.
 * Falls back to the system local date when timezone is null/undefined.
 */
const todayInTz = (timezone) => {
  if (!timezone) {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${dd}`;
  }
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric', month: '2-digit', day: '2-digit',
    }).format(new Date());
  } catch {
    return new Date().toISOString().slice(0, 10);
  }
};

/**
 * Compute the benchmark date range: always the last `windowDays` full calendar
 * days before today (local timezone), independent of any user-selected date.
 *
 *   endDate   = yesterday  (inclusive)
 *   startDate = endDate − (windowDays − 1) days  (inclusive)
 *
 * Example: today 2026-03-04, windowDays=30 → { start: '2026-02-02', end: '2026-03-03' }
 *
 * @param {number} [windowDays=HISTORY_WINDOW_DAYS]
 * @param {Date}   [_now=new Date()]  – injectable for testing
 * @returns {{ start: string, end: string }}  YYYY-MM-DD strings
 */
export const getBenchmarkRange = (windowDays = HISTORY_WINDOW_DAYS, _now = new Date()) => {
  const toLocal = (d) => {
    const y  = d.getFullYear();
    const m  = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${dd}`;
  };
  const end   = new Date(_now);
  end.setDate(end.getDate() - 1);                      // yesterday
  const start = new Date(end);
  start.setDate(start.getDate() - (windowDays - 1));   // endDate − 29 days
  return { start: toLocal(start), end: toLocal(end) };
};

const fmtDuration = (seconds) => {
  if (seconds === null || seconds === undefined) return '—';
  const s = Math.round(seconds);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return rem > 0 ? `${m}m ${rem}s` : `${m}m`;
};

/**
 * Calculate the percentage delta between today's avg and the historical avg.
 * Returns null when either value is unavailable.
 *
 * @param {number|null} todayAvg
 * @param {number|null} historyAvg
 * @returns {number|null}  positive = higher than history, negative = lower
 */
export const calcDelta = (todayAvg, historyAvg) => {
  if (todayAvg === null || todayAvg === undefined) return null;
  if (historyAvg === null || historyAvg === undefined) return null;
  if (historyAvg === 0) return null;
  return ((todayAvg - historyAvg) / historyAvg) * 100;
};

/**
 * Determine trend direction based on delta percentage.
 * Returns 'up' | 'down' | 'neutral' | null (no data).
 *
 * @param {number|null} deltaPct
 * @param {number} [tolerance=TREND_NEUTRAL_TOLERANCE_PCT]
 */
export const getTrendDirection = (deltaPct, tolerance = TREND_NEUTRAL_TOLERANCE_PCT) => {
  if (deltaPct === null || deltaPct === undefined) return null;
  if (Math.abs(deltaPct) <= tolerance) return 'neutral';
  return deltaPct > 0 ? 'up' : 'down';
};

/**
 * Compute anomaly score = |delta| (absolute percentage deviation).
 * Routes with no history get Infinity so they sort to the bottom.
 *
 * @param {number|null} deltaPct
 * @returns {number}
 */
export const calcAnomalyScore = (deltaPct) => {
  if (deltaPct === null || deltaPct === undefined) return Infinity;
  return Math.abs(deltaPct);
};

/** Format a YYYY-MM-DD date string for display (e.g. "Mon, 3 Mar 2026"). */
const fmtDay = (dateStr) => {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
  });
};

/* ─── sub-components ─────────────────────────────────────────────────────── */

const StatCard = ({ icon: Icon, label, value, sub }) => (
  <div className="ct-stat-card">
    <div className="ct-stat-icon"><Icon size={16} /></div>
    <div className="ct-stat-body">
      <p className="ct-stat-value">{value}</p>
      <p className="ct-stat-label">{label}</p>
      {sub && <p className="ct-stat-sub">{sub}</p>}
    </div>
  </div>
);

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="ct-tooltip">
      <p className="ct-tooltip-pair">
        <span className="ct-tooltip-from">{d.from_camera_name}</span>
        <ArrowRight size={12} className="ct-tooltip-arrow" />
        <span className="ct-tooltip-to">{d.to_camera_name}</span>
      </p>
      <div className="ct-tooltip-rows">
        <div className="ct-tooltip-row">
          <span>Transitions</span>
          <strong>{d.total_transitions.toLocaleString()}</strong>
        </div>
        {d.avg_duration_seconds !== null && (
          <div className="ct-tooltip-row">
            <span>Avg duration</span>
            <strong>{fmtDuration(d.avg_duration_seconds)}</strong>
          </div>
        )}
        {d.min_duration_seconds !== null && (
          <div className="ct-tooltip-row">
            <span>Min / Max</span>
            <strong>{fmtDuration(d.min_duration_seconds)} / {fmtDuration(d.max_duration_seconds)}</strong>
          </div>
        )}
        <div className="ct-tooltip-row">
          <span>Days active</span>
          <strong>{d.days_active}</strong>
        </div>
      </div>
    </div>
  );
};

/* ─── TrendArrow ─────────────────────────────────────────────────────────── */

/**
 * Renders a coloured directional arrow with an accessible label and a
 * hover tooltip showing today / history / delta.
 */
const TrendArrow = ({ todayAvg, historyAvg }) => {
  const deltaPct   = calcDelta(todayAvg, historyAvg);
  const direction  = getTrendDirection(deltaPct);

  if (direction === null) return null;

  const sign    = deltaPct > 0 ? '+' : '';
  const pctStr  = `${sign}${deltaPct.toFixed(1)}%`;
  const tooltip =
    `Today: ${fmtDuration(todayAvg)} | History Avg: ${fmtDuration(historyAvg)} | Delta: ${pctStr}`;

  let ariaLabel;
  if (direction === 'neutral') {
    ariaLabel = `Within ${TREND_NEUTRAL_TOLERANCE_PCT}% of historical average`;
  } else {
    const absPct = Math.abs(deltaPct).toFixed(1);
    ariaLabel = direction === 'up'
      ? `${absPct}% above historical average`
      : `${absPct}% below historical average`;
  }

  const iconProps = { size: 13, 'aria-hidden': true };

  return (
    <span
      className={`ct-trend-arrow ct-trend-arrow--${direction}`}
      title={tooltip}
      aria-label={ariaLabel}
      role="img"
    >
      {direction === 'up'      && <ArrowUp   {...iconProps} />}
      {direction === 'down'    && <ArrowDown {...iconProps} />}
      {direction === 'neutral' && <Minus     {...iconProps} />}
      <span className="ct-trend-pct">{pctStr}</span>
    </span>
  );
};

/* ─── HistorySkeleton ────────────────────────────────────────────────────── */

/** Skeleton placeholder shown while history data is loading. */
const HistorySkeleton = () => (
  <span className="ct-history-skeleton" aria-label="Loading historical average" />
);

/* ─── AnomalyBadge ───────────────────────────────────────────────────────── */

const AnomalyBadge = ({ rank }) => (
  <span className="ct-anomaly-badge" title={`Anomaly rank #${rank}`}>
    {rank}
  </span>
);

/* ─── TransitionRouteMap (full canvas) ───────────────────────────────────── */

/**
 * Colour-encode slowness using delta_pct vs history baseline.
 *   green  = normal (delta ≤ 15 %)
 *   amber  = elevated (15 < delta ≤ 40 %)
 *   red    = slow (delta > 40 %)
 *   indigo = no history available
 */
const routeColor = (deltaPct) => {
  if (deltaPct === null || deltaPct === undefined) return '#6366f1'; // no history
  if (deltaPct > 40)  return '#ef4444'; // red   – slow
  if (deltaPct > 15)  return '#f59e0b'; // amber – elevated
  return '#22c55e';                      // green – normal
};

/**
 * Scale stroke width between MIN_W and MAX_W based on a route's share of
 * total transitions.
 */
const STROKE_MIN = 2;
const STROKE_MAX = 9;
const routeStroke = (count, maxCount) => {
  if (!maxCount) return STROKE_MIN;
  const t = Math.sqrt(count / maxCount); // sqrt gives nicer perceptual scaling
  return STROKE_MIN + t * (STROKE_MAX - STROKE_MIN);
};

/**
 * Offset two anti-parallel routes (A→B and B→A) so they don't overlap.
 * Returns a quadratic bezier control point offset perpendicular to the line.
 */
const CURVE_OFFSET = 28;
const getCurveControl = (x1, y1, x2, y2, side) => {
  const mx = (x1 + x2) / 2;
  const my = (y1 + y2) / 2;
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.sqrt(dx * dx + dy * dy) || 1;
  // Perpendicular unit vector
  const px = -dy / len;
  const py =  dx / len;
  const sign = side === 1 ? 1 : -1;
  return { cx: mx + px * CURVE_OFFSET * sign, cy: my + py * CURVE_OFFSET * sign };
};

// Must match CameraSetupModal's SVG_WIDTH / SVG_HEIGHT exactly so that
// normalised positions (pos_x, pos_y ∈ [0,1]) map to the same pixel
// locations on the background image.
const MAP_W = 1000;
const MAP_H = 700;
const NODE_R = 16;

/**
 * Camera type colours — mirrors CameraSetupModal exactly.
 */
const TRM_CAMERA_COLORS = {
  entry: {
    fill:          '#16a34a',
    stroke:        'white',
    strokeHovered: '#86efac',
    glow:          'rgba(34,197,94,0.45)',
    ring:          '#4ade80',
    label:         'ENTRY',
    icon:          '▶',
    nameColor:     '#4ade80',
  },
  exit: {
    fill:          '#b45309',
    stroke:        'white',
    strokeHovered: '#fcd34d',
    glow:          'rgba(217,119,6,0.45)',
    ring:          '#fbbf24',
    label:         'EXIT',
    icon:          '◀',
    nameColor:     '#fbbf24',
  },
  regular: {
    fill:          '#4f46e5',
    stroke:        'white',
    strokeHovered: '#a5b4fc',
    glow:          null,
    ring:          null,
    label:         null,
    icon:          '📷',
    nameColor:     'rgba(255,255,255,0.85)',
  },
};

const getTrmCameraType = (cam) => {
  if (!cam) return 'regular';
  if (cam.is_entry_point === 'true' || cam.is_entry_point === true) return 'entry';
  if (cam.is_exit_point  === 'true' || cam.is_exit_point  === true) return 'exit';
  return 'regular';
};

/**
 * TransitionRouteMap
 *
 * Full-canvas SVG showing ALL camera nodes and ALL transition routes for the
 * selected day, drawn on the uploaded background image.
 *
 * Props:
 *   enrichedTransitions  {Array}   – enriched rows (with delta_pct, anomaly_score)
 *   config               {object}  – { cameras, hasBackgroundImage }
 *   cameras              {Array}   – raw camera list with is_entry_point / is_exit_point
 *   networkId            {string}
 *   onRouteClick         {func}    – optional: called with { fromId, toId }
 *   isInline             {boolean} – true when rendered in the constrained inline canvas
 *                                    (uses larger tooltip fonts for readability at small size)
 *   onConfigureMap       {func}    – optional: opens camera setup (for ghost preview overlay)
 *   onShowHelp           {func}    – optional: opens LPR Journey intro (for ghost preview overlay)
 */
const TransitionRouteMap = ({ enrichedTransitions, config, cameras = [], networkId, onRouteClick, isInline = false, onConfigureMap, onShowHelp }) => {
  const [bgUrl, setBgUrl]           = useState(null);
  const [bgLoading, setBgLoading]   = useState(false);
  const [bgImageReady, setBgImageReady] = useState(false); // true once browser finishes decoding the image
  const [hoveredRoute, setHoveredRoute] = useState(null); // "fromId|toId"
  const [tooltip, setTooltip]       = useState(null);     // { x, y, data }
  const svgRef    = useRef(null);
  const loadedRef = useRef(null);

  /* ── load background image ─────────────────────────────────────────────── */
  useEffect(() => {
    if (!config?.hasBackgroundImage || !networkId) return;
    if (loadedRef.current === networkId && bgUrl) return;
    loadedRef.current = networkId;
    setBgLoading(true);
    setBgImageReady(false);
    imageService.getImageUrl(`lpr-journey/${networkId}`, 7200)
      .then(res => { if (res.success) setBgUrl(res.url); })
      .catch(() => {})
      .finally(() => setBgLoading(false));
  }, [networkId, config?.hasBackgroundImage]); // eslint-disable-line react-hooks/exhaustive-deps

  /* When networkId changes, reset readiness so the spinner shows again */
  useEffect(() => { setBgImageReady(false); }, [networkId]);

  /* Whether the map content (pins + routes) should be visible */
  const mapContentReady = !config?.hasBackgroundImage || bgImageReady;

  /* ── derive camera nodes ───────────────────────────────────────────────── */
  const configCams = config?.cameras || [];

  // Build a lookup from camera_id → raw camera row (for entry/exit flags)
  const rawCameraMap = useMemo(() => {
    const m = {};
    cameras.forEach(c => { m[c.camera_id] = c; });
    return m;
  }, [cameras]);

  // All cameras that appear in transitions AND have positions
  const activeCameraIds = useMemo(() => {
    const ids = new Set();
    enrichedTransitions.forEach(r => { ids.add(r.from_camera_id); ids.add(r.to_camera_id); });
    return ids;
  }, [enrichedTransitions]);

  const nodes = useMemo(() => {
    return configCams
      .filter(c => c.pos_x != null && c.pos_y != null && activeCameraIds.has(c.camera_id))
      .map(c => {
        const raw  = rawCameraMap[c.camera_id] || null;
        const type = getTrmCameraType(raw);
        return {
          id:   c.camera_id,
          name: c.camera_name || c.camera_id,
          x:    c.pos_x * MAP_W,
          y:    c.pos_y * MAP_H,
          type,
          colors: TRM_CAMERA_COLORS[type],
        };
      });
  }, [configCams, activeCameraIds, rawCameraMap]);

  const nodeMap = useMemo(() => {
    const m = {};
    nodes.forEach(n => { m[n.id] = n; });
    return m;
  }, [nodes]);

  /* ── max transitions for stroke scaling ────────────────────────────────── */
  const maxCount = useMemo(
    () => enrichedTransitions.reduce((m, r) => Math.max(m, r.total_transitions), 0),
    [enrichedTransitions],
  );

  /* ── detect bi-directional pairs ───────────────────────────────────────── */
  const pairSet = useMemo(() => {
    const s = new Set();
    enrichedTransitions.forEach(r => s.add(`${r.from_camera_id}|${r.to_camera_id}`));
    return s;
  }, [enrichedTransitions]);

  /* ── check for empty state ─────────────────────────────────────────────── */
  const hasPositions = configCams.some(c => c.pos_x != null && c.pos_y != null);

  if (!hasPositions) {
    return (
      <LprJourneyGhostPreview
        imageSrc={lprJourneyCameraTransitionImg}
        title="Transition Route Map preview"
        description="Place your cameras on a site map to see routes between them. Use Configure Map to position each camera — once set up, you'll see traffic flow and travel times between camera pairs."
        primaryLabel="Configure Map"
        onPrimaryClick={onConfigureMap}
        onHelpClick={onShowHelp}
      />
    );
  }

  const isBiDir = (fromId, toId) => pairSet.has(`${toId}|${fromId}`);

  /* ── tooltip mouse handler ─────────────────────────────────────────────── */
  const handleRouteMouseMove = (e, row) => {
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const scaleX = MAP_W / rect.width;
    const scaleY = MAP_H / rect.height;
    setTooltip({
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top)  * scaleY,
      data: row,
    });
  };

  const handleRouteMouseLeave = () => {
    setHoveredRoute(null);
    setTooltip(null);
  };

  /* ── render ─────────────────────────────────────────────────────────────── */
  return (
    <div className="ct-trm-wrap">
      {/* Legend */}
      <div className="ct-trm-legend">
        <span className="ct-trm-legend-title">Transit time vs baseline:</span>
        <span className="ct-trm-legend-item ct-trm-legend-green">
          <span className="ct-trm-legend-swatch" style={{ background: '#22c55e' }} />
          Normal (≤+15%)
        </span>
        <span className="ct-trm-legend-item ct-trm-legend-amber">
          <span className="ct-trm-legend-swatch" style={{ background: '#f59e0b' }} />
          Elevated (+15–40%)
        </span>
        <span className="ct-trm-legend-item ct-trm-legend-red">
          <span className="ct-trm-legend-swatch" style={{ background: '#ef4444' }} />
          Slow (&gt;+40%)
        </span>
        <span className="ct-trm-legend-item ct-trm-legend-indigo">
          <span className="ct-trm-legend-swatch" style={{ background: '#6366f1' }} />
          No baseline
        </span>
        <span className="ct-trm-legend-divider" />
        <span className="ct-trm-legend-item">
          <span className="ct-trm-legend-thickness" />
          Thickness = volume
        </span>
      </div>

      {/* Loading spinner — shown while background image is being fetched / decoded */}
      {(bgLoading || (bgUrl && !bgImageReady)) && (
        <div className="ct-map-loading-overlay">
          <div className="ct-spinner ct-map-spinner" />
          <span className="ct-map-loading-text">Loading map…</span>
        </div>
      )}

      <svg
        ref={svgRef}
        viewBox={`0 0 ${MAP_W} ${MAP_H}`}
        width="100%"
        className="ct-trm-svg"
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          {/* One arrowhead marker per colour */}
          {['#22c55e', '#f59e0b', '#ef4444', '#6366f1'].map(color => {
            const id = `trm-arrow-${color.replace('#', '')}`;
            return (
              <marker
                key={id}
                id={id}
                markerWidth="7" markerHeight="7"
                refX="5" refY="3.5"
                orient="auto"
              >
                <path d="M0,0 L0,7 L7,3.5 z" fill={color} />
              </marker>
            );
          })}
          {/* Hovered arrowhead (white) */}
          <marker id="trm-arrow-hover" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <path d="M0,0 L0,7 L7,3.5 z" fill="white" />
          </marker>
          {/* Glow filter */}
          <filter id="trm-glow" x="-60%" y="-60%" width="220%" height="220%">
            <feGaussianBlur stdDeviation="4" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          {/* Node shadow */}
          <filter id="trm-node-shadow" x="-40%" y="-40%" width="180%" height="180%">
            <feDropShadow dx="0" dy="2" stdDeviation="3" floodColor="rgba(0,0,0,0.6)" />
          </filter>
        </defs>

        {/* Background fill */}
        <rect x="0" y="0" width={MAP_W} height={MAP_H} fill="rgba(8,12,24,0.92)" />

        {/* Grid (shown when no bg image) */}
        {!bgUrl && Array.from({ length: 8 }, (_, i) => (
          <line key={`vg${i}`}
            x1={((i + 1) * MAP_W) / 9} y1="0"
            x2={((i + 1) * MAP_W) / 9} y2={MAP_H}
            stroke="rgba(255,255,255,0.03)" strokeWidth="1"
          />
        ))}
        {!bgUrl && Array.from({ length: 5 }, (_, i) => (
          <line key={`hg${i}`}
            x1="0" y1={((i + 1) * MAP_H) / 6}
            x2={MAP_W} y2={((i + 1) * MAP_H) / 6}
            stroke="rgba(255,255,255,0.03)" strokeWidth="1"
          />
        ))}

        {/* Background map image — onLoad fires once the browser has decoded it */}
        {bgUrl && (
          <image
            href={bgUrl}
            x="0" y="0"
            width={MAP_W} height={MAP_H}
            opacity="0.4"
            preserveAspectRatio="xMidYMid meet"
            onLoad={() => setBgImageReady(true)}
          />
        )}

        {/* ── Routes (deferred until background image is ready) ───────────── */}
        {mapContentReady && enrichedTransitions.map(row => {
          const from = nodeMap[row.from_camera_id];
          const to   = nodeMap[row.to_camera_id];
          if (!from || !to) return null;

          const key       = `${row.from_camera_id}|${row.to_camera_id}`;
          const isHovered = hoveredRoute === key;
          const color     = isHovered ? '#ffffff' : routeColor(row.delta_pct);
          const stroke    = routeStroke(row.total_transitions, maxCount);
          const arrowId   = isHovered
            ? 'trm-arrow-hover'
            : `trm-arrow-${routeColor(row.delta_pct).replace('#', '')}`;

          // Offset anti-parallel pairs with a curve
          const biDir = isBiDir(row.from_camera_id, row.to_camera_id);
          const side  = biDir
            ? (row.from_camera_id < row.to_camera_id ? 1 : -1)
            : 0;

          const dx   = to.x - from.x;
          const dy   = to.y - from.y;
          const dist = Math.sqrt(dx * dx + dy * dy) || 1;
          const ux   = dx / dist;
          const uy   = dy / dist;

          // Start/end points pulled back from node edges
          const x1 = from.x + ux * (NODE_R + 5);
          const y1 = from.y + uy * (NODE_R + 5);
          const x2 = to.x   - ux * (NODE_R + 10);
          const y2 = to.y   - uy * (NODE_R + 10);

          let pathD;
          if (biDir) {
            const { cx, cy } = getCurveControl(x1, y1, x2, y2, side);
            pathD = `M ${x1} ${y1} Q ${cx} ${cy} ${x2} ${y2}`;
          } else {
            pathD = `M ${x1} ${y1} L ${x2} ${y2}`;
          }

          return (
            <g key={key}>
              {/* Wide invisible hit area */}
              <path
                d={pathD}
                fill="none"
                stroke="transparent"
                strokeWidth={Math.max(stroke + 12, 18)}
                style={{ cursor: 'pointer' }}
                onMouseEnter={() => setHoveredRoute(key)}
                onMouseMove={e => handleRouteMouseMove(e, row)}
                onMouseLeave={handleRouteMouseLeave}
                onClick={() => onRouteClick?.({ fromId: row.from_camera_id, toId: row.to_camera_id })}
              />
              {/* Glow halo */}
              {isHovered && (
                <path
                  d={pathD}
                  fill="none"
                  stroke="rgba(255,255,255,0.18)"
                  strokeWidth={stroke + 8}
                  strokeLinecap="round"
                  style={{ pointerEvents: 'none' }}
                />
              )}
              {/* Main route line */}
              <path
                d={pathD}
                fill="none"
                stroke={color}
                strokeWidth={stroke}
                strokeLinecap="round"
                opacity={isHovered ? 1 : (hoveredRoute ? 0.35 : 0.82)}
                markerEnd={`url(#${arrowId})`}
                style={{ pointerEvents: 'none', transition: 'opacity 0.15s' }}
              />
            </g>
          );
        })}

        {/* ── Camera nodes (deferred until background image is ready) ───── */}
        {mapContentReady && nodes.map(node => {
          const isInvolved = hoveredRoute
            ? hoveredRoute.startsWith(node.id + '|') || hoveredRoute.endsWith('|' + node.id)
            : false;
          const { colors, type } = node;
          const stroke = isInvolved ? colors.strokeHovered : colors.stroke;

          return (
            <g key={node.id}>
              {/* Glow halo for entry / exit */}
              {colors.glow && (
                <circle
                  cx={node.x} cy={node.y}
                  r={NODE_R + 7}
                  fill={isInvolved ? colors.glow : colors.glow.replace('0.45', '0.25')}
                  style={{ pointerEvents: 'none', transition: 'fill 0.15s' }}
                />
              )}
              {/* Dashed outer ring for entry / exit */}
              {colors.ring && (
                <circle
                  cx={node.x} cy={node.y}
                  r={NODE_R + 5}
                  fill="none"
                  stroke={colors.ring}
                  strokeWidth="1.5"
                  strokeDasharray="4 3"
                  opacity={isInvolved ? 1 : 0.6}
                  style={{ pointerEvents: 'none', transition: 'opacity 0.15s' }}
                />
              )}
              {/* Drop shadow */}
              <circle
                cx={node.x + 2} cy={node.y + 2}
                r={NODE_R}
                fill="rgba(0,0,0,0.35)"
                style={{ pointerEvents: 'none' }}
              />
              {/* Main node circle */}
              <circle
                cx={node.x} cy={node.y}
                r={NODE_R}
                fill={colors.fill}
                stroke={stroke}
                strokeWidth={isInvolved ? 3 : 2}
                opacity={isInvolved ? 1 : (hoveredRoute ? 0.7 : 1)}
                style={{ transition: 'stroke 0.15s, opacity 0.15s' }}
              />
              {/* Icon (▶ / ◀ / 📷) */}
              <text
                x={node.x}
                y={node.y - (colors.label ? 5 : 0)}
                fill="white"
                fontSize={type === 'regular' ? '14' : '11'}
                textAnchor="middle"
                dominantBaseline="central"
                style={{ pointerEvents: 'none', userSelect: 'none' }}
              >
                {colors.icon}
              </text>
              {/* ENTRY / EXIT label inside node */}
              {colors.label && (
                <text
                  x={node.x}
                  y={node.y + 10}
                  fill="white"
                  fontSize="7"
                  fontWeight="800"
                  letterSpacing="0.5"
                  textAnchor="middle"
                  dominantBaseline="central"
                  style={{ pointerEvents: 'none', userSelect: 'none' }}
                >
                  {colors.label}
                </text>
              )}
              {/* Camera name below node */}
              <text
                x={node.x}
                y={node.y + NODE_R + (colors.ring ? 12 : 10)}
                fill={isInvolved ? '#e2e8f0' : colors.nameColor}
                fontSize="9"
                fontWeight={isInvolved ? '700' : '600'}
                textAnchor="middle"
                dominantBaseline="central"
                style={{ pointerEvents: 'none', userSelect: 'none' }}
              >
                {node.name.length > 14 ? node.name.slice(0, 13) + '…' : node.name}
              </text>
            </g>
          );
        })}

        {/* ── SVG Tooltip ────────────────────────────────────────────────── */}
        {mapContentReady && tooltip && (() => {
          const d = tooltip.data;

          // Inline (normal) view uses larger text for readability at constrained size.
          // Fullscreen view gets a richer, more detailed tooltip.
          const TW        = isInline ? 260 : 250;
          const fzHeader  = isInline ? 14   : 11;
          const fzLabel   = isInline ? 12   : 10;
          const fzValue   = isInline ? 13   : 10;
          const rowStride = isInline ? 24   : 19;
          const firstRowY = isInline ? 52   : 44;
          const headerY   = isInline ? 20   : 18;
          const dividerY  = isInline ? 32   : 27;

          const color = routeColor(d.delta_pct);
          const sign  = d.delta_pct !== null && d.delta_pct > 0 ? '+' : '';
          const deltaStr = d.delta_pct !== null
            ? `${sign}${d.delta_pct.toFixed(1)}% vs baseline`
            : 'No baseline';

          // Full-screen: richer rows with all available metrics
          // Inline: keep it compact — just the two most actionable fields
          const rows = isInline
            ? [
                ['Avg duration', fmtDuration(d.avg_duration_seconds)],
                ['vs Baseline',  deltaStr],
              ]
            : [
                ['Transitions',   d.total_transitions.toLocaleString()],
                ['Avg duration',  fmtDuration(d.avg_duration_seconds)],
                ['Min / Max',     d.min_duration_seconds !== null
                                    ? `${fmtDuration(d.min_duration_seconds)} / ${fmtDuration(d.max_duration_seconds)}`
                                    : '—'],
                ['vs Baseline',   deltaStr],
                ['History avg',   d.history_avg !== null ? fmtDuration(d.history_avg) : '—'],
                ['History min/max', d.history_min !== null
                                    ? `${fmtDuration(d.history_min)} / ${fmtDuration(d.history_max)}`
                                    : '—'],
              ];

          const sectionPad = !isInline && rows.length > 3 ? 8 : 0;
          const TH = firstRowY + rows.length * rowStride + sectionPad + (isInline ? 12 : 10);

          // Clamp so tooltip stays inside canvas
          const tx = Math.min(tooltip.x + 14, MAP_W - TW - 8);
          const ty = Math.min(tooltip.y - 10, MAP_H - TH - 8);

          return (
            <g style={{ pointerEvents: 'none' }}>
              {/* Shadow */}
              <rect x={tx + 2} y={ty + 2} width={TW} height={TH} rx="8" fill="rgba(0,0,0,0.45)" />
              {/* Background */}
              <rect x={tx} y={ty} width={TW} height={TH} rx="8"
                fill="#1e293b" stroke="rgba(255,255,255,0.12)" strokeWidth="1" />
              {/* Colour accent bar */}
              <rect x={tx} y={ty} width={4} height={TH} rx="2" fill={color} />

              {/* From → To header */}
              <text x={tx + 14} y={ty + headerY} fill="#e2e8f0" fontSize={fzHeader} fontWeight="700">
                {d.from_camera_name.length > 14 ? d.from_camera_name.slice(0, 13) + '…' : d.from_camera_name}
                {' → '}
                {d.to_camera_name.length > 14 ? d.to_camera_name.slice(0, 13) + '…' : d.to_camera_name}
              </text>

              {/* Divider */}
              <line x1={tx + 14} y1={ty + dividerY} x2={tx + TW - 14} y2={ty + dividerY}
                stroke="rgba(255,255,255,0.09)" strokeWidth="1" />

              {/* Stats rows */}
              {rows.map(([label, val], idx) => {
                // In fullscreen mode, draw a subtle section divider centred in
                // the gap before the baseline/history block (row index 3 = "vs Baseline")
                const sectionBreak = !isInline && idx === 3;
                const extraPad = (!isInline && idx >= 3) ? 8 : 0;
                const rowY = ty + firstRowY + idx * rowStride + extraPad;
                return (
                  <g key={label}>
                    {sectionBreak && (
                      <line
                        x1={tx + 14} y1={rowY - Math.round(rowStride / 2)}
                        x2={tx + TW - 14} y2={rowY - Math.round(rowStride / 2)}
                        stroke="rgba(255,255,255,0.1)" strokeWidth="1"
                      />
                    )}
                    <text x={tx + 14} y={rowY}
                      fill="#94a3b8" fontSize={fzLabel} fontWeight="500">{label}</text>
                    <text x={tx + TW - 14} y={rowY}
                      fill="#f1f5f9" fontSize={fzValue} fontWeight="700" textAnchor="end">{val}</text>
                  </g>
                );
              })}
            </g>
          );
        })()}

        {/* Border */}
        <rect x="0.5" y="0.5" width={MAP_W - 1} height={MAP_H - 1}
          fill="none" stroke="rgba(99,102,241,0.15)" strokeWidth="1" rx="12" />
      </svg>
    </div>
  );
};

/* ─── RouteMap mini-map ──────────────────────────────────────────────────── */

const MINI_W = 400;
const MINI_H = 280;
const MINI_NODE_R = 18;

/**
 * RouteMap
 * Renders a small SVG map showing the from/to cameras and a directed arrow
 * between them, using the saved normalized positions from config.
 *
 * Props:
 *   fromCameraId   {string}
 *   toCameraId     {string}
 *   fromCameraName {string}
 *   toCameraName   {string}
 *   config         {object}  - { cameras: [{camera_id, pos_x, pos_y}], hasBackgroundImage }
 *   networkId      {string}
 */
const RouteMap = ({ fromCameraId, toCameraId, fromCameraName, toCameraName, config, networkId }) => {
  const [bgUrl, setBgUrl]               = useState(null);
  const [bgLoading, setBgLoading]       = useState(false);
  const [bgImageReady, setBgImageReady] = useState(false);
  const loadedNetworkRef = useRef(null);

  // Load background image once per networkId
  useEffect(() => {
    if (!config?.hasBackgroundImage || !networkId) return;
    if (loadedNetworkRef.current === networkId && bgUrl) return;
    loadedNetworkRef.current = networkId;
    setBgLoading(true);
    setBgImageReady(false);
    imageService.getImageUrl(`lpr-journey/${networkId}`, 7200)
      .then(res => { if (res.success) setBgUrl(res.url); })
      .catch(() => {})
      .finally(() => setBgLoading(false));
  }, [networkId, config?.hasBackgroundImage]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setBgImageReady(false); }, [networkId]);

  const mapContentReady = !config?.hasBackgroundImage || bgImageReady;

  // Look up normalized positions from config
  const configCams = config?.cameras || [];
  const fromCam = configCams.find(c => c.camera_id === fromCameraId);
  const toCam   = configCams.find(c => c.camera_id === toCameraId);

  const hasPositions = fromCam?.pos_x != null && toCam?.pos_x != null;

  // Convert normalized [0,1] → SVG pixel coords.
  const toSVG = (normX, normY) => ({
    x: normX * MINI_W,
    y: normY * MINI_H,
  });

  if (!hasPositions) {
    return (
      <div className="ct-routemap-unavailable">
        <MapPin size={14} />
        <span>Map positions not configured for these cameras</span>
      </div>
    );
  }

  const EDGE = MINI_NODE_R + 2;
  const clamp = (pt) => ({
    x: Math.max(EDGE, Math.min(pt.x, MINI_W - EDGE)),
    y: Math.max(EDGE, Math.min(pt.y, MINI_H - EDGE)),
  });

  const from = clamp(toSVG(fromCam.pos_x, fromCam.pos_y));
  const to   = clamp(toSVG(toCam.pos_x,   toCam.pos_y));

  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const dist = Math.sqrt(dx * dx + dy * dy) || 1;
  const ux = dx / dist;
  const uy = dy / dist;
  const arrowStart = { x: from.x + ux * (MINI_NODE_R + 4), y: from.y + uy * (MINI_NODE_R + 4) };
  const arrowEnd   = { x: to.x   - ux * (MINI_NODE_R + 8), y: to.y   - uy * (MINI_NODE_R + 8) };

  const mid = { x: (from.x + to.x) / 2, y: (from.y + to.y) / 2 };

  return (
    <div className="ct-routemap-wrap">
      {/* Loading spinner — shown while background image is being fetched / decoded */}
      {(bgLoading || (bgUrl && !bgImageReady)) && (
        <div className="ct-map-loading-overlay ct-map-loading-overlay--mini">
          <div className="ct-spinner ct-map-spinner" />
        </div>
      )}

      <svg
        viewBox={`0 0 ${MINI_W} ${MINI_H}`}
        width="100%"
        className="ct-routemap-svg"
        preserveAspectRatio="xMidYMid meet"
      >
        {/* Background */}
        <rect x="0" y="0" width={MINI_W} height={MINI_H} fill="rgba(10,15,30,0.85)" rx="10" />

        {/* Grid lines (shown when no bg image) */}
        {!bgUrl && Array.from({ length: 5 }, (_, i) => (
          <line
            key={`v${i}`}
            x1={((i + 1) * MINI_W) / 6} y1="0"
            x2={((i + 1) * MINI_W) / 6} y2={MINI_H}
            stroke="rgba(255,255,255,0.04)" strokeWidth="1"
          />
        ))}
        {!bgUrl && Array.from({ length: 3 }, (_, i) => (
          <line
            key={`h${i}`}
            x1="0" y1={((i + 1) * MINI_H) / 4}
            x2={MINI_W} y2={((i + 1) * MINI_H) / 4}
            stroke="rgba(255,255,255,0.04)" strokeWidth="1"
          />
        ))}

        {/* Background map image — onLoad fires once the browser has decoded it */}
        {bgUrl && (
          <image
            href={bgUrl}
            x="0" y="0"
            width={MINI_W} height={MINI_H}
            opacity="0.45"
            preserveAspectRatio="xMidYMid meet"
            onLoad={() => setBgImageReady(true)}
          />
        )}

        {/* Defs: arrowhead marker */}
        <defs>
          <marker
            id="ct-arrow"
            markerWidth="8" markerHeight="8"
            refX="6" refY="3"
            orient="auto"
          >
            <path d="M0,0 L0,6 L8,3 z" fill="#6366f1" />
          </marker>
          {/* Glow filter for nodes */}
          <filter id="ct-glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* ── Route arrow + camera nodes (deferred until background is ready) */}
        {mapContentReady && dist > (MINI_NODE_R * 2 + 16) && (
          <>
            {/* Glow line */}
            <line
              x1={arrowStart.x} y1={arrowStart.y}
              x2={arrowEnd.x}   y2={arrowEnd.y}
              stroke="rgba(99,102,241,0.25)"
              strokeWidth="6"
              strokeLinecap="round"
            />
            {/* Main arrow line */}
            <line
              x1={arrowStart.x} y1={arrowStart.y}
              x2={arrowEnd.x}   y2={arrowEnd.y}
              stroke="#6366f1"
              strokeWidth="2"
              strokeDasharray="6 3"
              markerEnd="url(#ct-arrow)"
              strokeLinecap="round"
            />
            {/* Distance dot at midpoint */}
            <circle cx={mid.x} cy={mid.y} r="3" fill="#6366f1" opacity="0.8" />
          </>
        )}

        {/* FROM camera node */}
        {mapContentReady && (
        <g filter="url(#ct-glow)">
          <circle cx={from.x} cy={from.y} r={MINI_NODE_R + 5} fill="rgba(34,197,94,0.18)" />
          <circle cx={from.x} cy={from.y} r={MINI_NODE_R + 3} fill="none" stroke="#4ade80" strokeWidth="1.5" strokeDasharray="4 3" />
          <circle cx={from.x + 1.5} cy={from.y + 1.5} r={MINI_NODE_R} fill="rgba(0,0,0,0.3)" />
          <circle cx={from.x} cy={from.y} r={MINI_NODE_R} fill="#16a34a" stroke="white" strokeWidth="2" />
          <text x={from.x} y={from.y - 3} fill="white" fontSize="10" textAnchor="middle" dominantBaseline="central" style={{ pointerEvents: 'none', userSelect: 'none' }}>▶</text>
          <text x={from.x} y={from.y + 9} fill="white" fontSize="6" fontWeight="800" letterSpacing="0.5" textAnchor="middle" dominantBaseline="central" style={{ pointerEvents: 'none', userSelect: 'none' }}>FROM</text>
        </g>
        )}
        {mapContentReady && (
        <text
          x={from.x} y={from.y + MINI_NODE_R + 14}
          fill="#4ade80" fontSize="9" fontWeight="600"
          textAnchor="middle" dominantBaseline="central"
          style={{ pointerEvents: 'none', userSelect: 'none' }}
        >
          {fromCameraName.length > 14 ? fromCameraName.slice(0, 13) + '…' : fromCameraName}
        </text>
        )}

        {/* TO camera node */}
        {mapContentReady && (
        <g filter="url(#ct-glow)">
          <circle cx={to.x} cy={to.y} r={MINI_NODE_R + 5} fill="rgba(217,119,6,0.18)" />
          <circle cx={to.x} cy={to.y} r={MINI_NODE_R + 3} fill="none" stroke="#fbbf24" strokeWidth="1.5" strokeDasharray="4 3" />
          <circle cx={to.x + 1.5} cy={to.y + 1.5} r={MINI_NODE_R} fill="rgba(0,0,0,0.3)" />
          <circle cx={to.x} cy={to.y} r={MINI_NODE_R} fill="#b45309" stroke="white" strokeWidth="2" />
          <text x={to.x} y={to.y - 3} fill="white" fontSize="10" textAnchor="middle" dominantBaseline="central" style={{ pointerEvents: 'none', userSelect: 'none' }}>◀</text>
          <text x={to.x} y={to.y + 9} fill="white" fontSize="6" fontWeight="800" letterSpacing="0.5" textAnchor="middle" dominantBaseline="central" style={{ pointerEvents: 'none', userSelect: 'none' }}>TO</text>
        </g>
        )}
        {mapContentReady && (
        <text
          x={to.x} y={to.y + MINI_NODE_R + 14}
          fill="#fbbf24" fontSize="9" fontWeight="600"
          textAnchor="middle" dominantBaseline="central"
          style={{ pointerEvents: 'none', userSelect: 'none' }}
        >
          {toCameraName.length > 14 ? toCameraName.slice(0, 13) + '…' : toCameraName}
        </text>
        )}

        {/* Border */}
        <rect x="0.5" y="0.5" width={MINI_W - 1} height={MINI_H - 1} fill="none" stroke="rgba(99,102,241,0.2)" strokeWidth="1" rx="10" />
      </svg>

      {/* Legend */}
      <div className="ct-routemap-legend">
        <span className="ct-routemap-legend-from">
          <span className="ct-routemap-legend-dot ct-routemap-legend-dot-from" />
          From: {fromCameraName}
        </span>
        <span className="ct-routemap-legend-arrow">→</span>
        <span className="ct-routemap-legend-to">
          <span className="ct-routemap-legend-dot ct-routemap-legend-dot-to" />
          To: {toCameraName}
        </span>
      </div>
    </div>
  );
};

/* ─── main component ─────────────────────────────────────────────────────── */

/**
 * CameraTransitions
 *
 * Props:
 *   networkId        {string}   - selected network ID
 *   networkName      {string}   - display name
 *   networkTimezone  {string}   - IANA timezone for the network (e.g. 'Australia/Sydney')
 *   config           {object}   - camera map config with positions
 *   cameras          {Array}    - raw camera list with is_entry_point / is_exit_point flags
 *   selectedDay      {string}   - optional YYYY-MM-DD; when provided the date navigator
 *                                 is controlled externally (shared with other sections)
 *   onDayChange      {function} - optional; called with new YYYY-MM-DD when user navigates
 *   onConfigureMap   {function} - optional; opens the camera setup modal
 *   onShowHelp       {function} - optional; opens the LPR Journey feature intro
 */
const CameraTransitions = ({ networkId, networkName, networkTimezone, config, cameras = [], selectedDay: selectedDayProp, onDayChange, onConfigureMap, onShowHelp }) => {
  const [transitions, setTransitions]       = useState([]);
  const [isLoading, setIsLoading]           = useState(false);
  const [error, setError]                   = useState(null);

  // Historical baseline state
  const [historyMap, setHistoryMap]         = useState({});   // key: "fromId|toId" → { avg, min, max }
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError]     = useState(null);

  /**
   * Currently viewed day (YYYY-MM-DD).
   * When selectedDayProp is provided (controlled mode), internal state is ignored
   * and we always use the prop value. The internal state is kept as a fallback for
   * standalone usage (no prop passed).
   */
  const [internalDay, setInternalDay]       = useState(() => todayInTz(networkTimezone));
  const selectedDay = selectedDayProp !== undefined ? selectedDayProp : internalDay;

  // 'anomaly' is the default sort; user can switch via column headers
  const [sortBy, setSortBy]                 = useState('anomaly');
  const [filterFrom, setFilterFrom]         = useState('');
  const [filterTo, setFilterTo]             = useState('');
  const [expandedRow, setExpandedRow]       = useState(null);
  const [isMapFullscreen, setIsMapFullscreen] = useState(false);

  const ROUTES_PAGE_SIZE = 10;
  const [routesPage, setRoutesPage]         = useState(1);

  /* ── ESC closes fullscreen map ─────────────────────────────────────────── */
  useEffect(() => {
    if (!isMapFullscreen) return;
    const onKey = (e) => { if (e.key === 'Escape') setIsMapFullscreen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isMapFullscreen]);

  /* ── lock body scroll while fullscreen map is open ─────────────────────── */
  useEffect(() => {
    document.body.style.overflow = isMapFullscreen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isMapFullscreen]);

  /* ── fetch transitions ──────────────────────────────────────────────────── */
  const fetchData = useCallback(async () => {
    if (!networkId) return;
    setTransitions([]);
    setIsLoading(true);
    setError(null);
    try {
      const data = await lprJourneyAPI.getCameraTransitions(networkId, selectedDay, selectedDay);
      setTransitions(data);
    } catch (err) {
      setError(err.message || 'Failed to load transition data');
    } finally {
      setIsLoading(false);
    }
  }, [networkId, selectedDay]);

  useEffect(() => { fetchData(); }, [fetchData]);

  /* ── fetch historical baseline (always anchored to today, not selectedDay) ─ */
  const fetchHistory = useCallback(async () => {
    if (!networkId) return;
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const data = await lprJourneyAPI.getHistoricalTransitions(networkId, HISTORY_WINDOW_DAYS);
      const map = {};
      data.forEach(r => {
        map[`${r.from_camera_id}|${r.to_camera_id}`] = {
          avg: r.history_avg_duration_seconds,
          min: r.history_min_duration_seconds ?? null,
          max: r.history_max_duration_seconds ?? null,
        };
      });
      setHistoryMap(map);
    } catch (err) {
      setHistoryError(err.message || 'Failed to load historical data');
    } finally {
      setHistoryLoading(false);
    }
  }, [networkId]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  /* ── day navigation ─────────────────────────────────────────────────────── */
  const shiftDay = (delta) => {
    const computeNext = (prev) => {
      const [y, m, d] = prev.split('-').map(Number);
      const date = new Date(y, m - 1, d);
      date.setDate(date.getDate() + delta);
      // Don't allow navigating past "today" in the network's timezone
      const todayStr = todayInTz(networkTimezone);
      const [ty, tm, td] = todayStr.split('-').map(Number);
      const todayDate = new Date(ty, tm - 1, td);
      if (date > todayDate) return prev;
      const yy = date.getFullYear();
      const mm = String(date.getMonth() + 1).padStart(2, '0');
      const dd = String(date.getDate()).padStart(2, '0');
      return `${yy}-${mm}-${dd}`;
    };

    if (onDayChange) {
      onDayChange(computeNext(selectedDay));
    } else {
      setInternalDay(prev => computeNext(prev));
    }
  };

  const jumpToToday = () => {
    const t = todayInTz(networkTimezone);
    if (onDayChange) {
      onDayChange(t);
    } else {
      setInternalDay(t);
    }
  };

  const isToday = selectedDay === todayInTz(networkTimezone);

  /* ── derived data ───────────────────────────────────────────────────────── */

  /**
   * Enrich each transition row with:
   *   history_avg  – from historyMap
   *   delta_pct    – % deviation from history
   *   anomaly_score – |delta_pct| (Infinity when no history)
   */
  const enriched = useMemo(() => transitions.map(r => {
    const historyEntry = historyMap[`${r.from_camera_id}|${r.to_camera_id}`] ?? null;
    const historyAvg   = historyEntry?.avg ?? null;
    const historyMin   = historyEntry?.min ?? null;
    const historyMax   = historyEntry?.max ?? null;
    const deltaPct     = calcDelta(r.avg_duration_seconds, historyAvg);
    return {
      ...r,
      history_avg:    historyAvg,
      history_min:    historyMin,
      history_max:    historyMax,
      delta_pct:      deltaPct,
      anomaly_score:  calcAnomalyScore(deltaPct),
    };
  }), [transitions, historyMap]);

  const filtered = useMemo(() => {
    let rows = [...enriched];
    if (filterFrom) rows = rows.filter(r =>
      r.from_camera_name.toLowerCase().includes(filterFrom.toLowerCase()) ||
      r.from_camera_id.toLowerCase().includes(filterFrom.toLowerCase())
    );
    if (filterTo) rows = rows.filter(r =>
      r.to_camera_name.toLowerCase().includes(filterTo.toLowerCase()) ||
      r.to_camera_id.toLowerCase().includes(filterTo.toLowerCase())
    );

    if (sortBy === 'anomaly') {
      // Rank 1 = most anomalous; routes with no history fall to bottom
      rows.sort((a, b) => {
        const aInf = a.anomaly_score === Infinity;
        const bInf = b.anomaly_score === Infinity;
        if (aInf && bInf) return 0;
        if (aInf) return 1;   // a (no history) → bottom
        if (bInf) return -1;  // b (no history) → bottom
        return b.anomaly_score - a.anomaly_score; // highest deviation first
      });
    } else if (sortBy === 'duration') {
      rows.sort((a, b) => (b.avg_duration_seconds ?? 0) - (a.avg_duration_seconds ?? 0));
    } else if (sortBy === 'transitions') {
      rows.sort((a, b) => b.total_transitions - a.total_transitions);
    } else if (sortBy === 'history') {
      rows.sort((a, b) => (b.history_avg ?? 0) - (a.history_avg ?? 0));
    }

    return rows;
  }, [enriched, filterFrom, filterTo, sortBy]);

  /* ── reset to page 1 when filters or sort change ────────────────────────── */
  useEffect(() => { setRoutesPage(1); }, [filterFrom, filterTo, sortBy]);

  const routesTotalPages = Math.max(1, Math.ceil(filtered.length / ROUTES_PAGE_SIZE));
  const routesPageSafe   = Math.min(routesPage, routesTotalPages);
  const filteredPage     = filtered.slice(
    (routesPageSafe - 1) * ROUTES_PAGE_SIZE,
    routesPageSafe * ROUTES_PAGE_SIZE,
  );

  /* ── summary stats ──────────────────────────────────────────────────────── */
  const totalTransitions = useMemo(() =>
    transitions.reduce((s, r) => s + r.total_transitions, 0),
  [transitions]);

  const avgDuration = useMemo(() => {
    const withDur = transitions.filter(r => r.avg_duration_seconds !== null);
    if (!withDur.length) return null;
    return withDur.reduce((s, r) => s + r.avg_duration_seconds, 0) / withDur.length;
  }, [transitions]);

  const uniqueCameras = useMemo(() => {
    const ids = new Set();
    transitions.forEach(r => { ids.add(r.from_camera_id); ids.add(r.to_camera_id); });
    return ids.size;
  }, [transitions]);

  /* ── column header helper ───────────────────────────────────────────────── */
  const SortableTh = ({ colKey, children, className = '' }) => (
    <th
      className={`ct-th ct-th-sortable ${sortBy === colKey ? 'ct-th-active' : ''} ${className}`}
      onClick={() => setSortBy(colKey)}
      tabIndex={0}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') setSortBy(colKey); }}
      aria-sort={sortBy === colKey ? 'ascending' : 'none'}
    >
      {children}
      {sortBy === colKey && <span className="ct-th-sort-indicator" aria-hidden="true">▲</span>}
    </th>
  );

  /* ── render ─────────────────────────────────────────────────────────────── */
  return (
    <div className="ct-container">

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="ct-header">
        <div className="ct-header-left">
          <h3 className="ct-title">
            <TrendingUp size={16} className="ct-title-icon" />
            Camera Transitions
            {networkName && <span className="ct-title-network">— {networkName}</span>}
          </h3>
          <p className="ct-subtitle">
            How vehicles move between cameras in this network
          </p>
        </div>
        <button
          className="ct-refresh-btn"
          onClick={() => { fetchData(); fetchHistory(); }}
          disabled={isLoading}
          title="Refresh"
        >
          <RefreshCw size={14} className={isLoading ? 'ct-spin' : ''} />
        </button>
      </div>

      {/* ── Day sliding-window controls ──────────────────────────────────────── */}
      <div className="ct-controls">
        <div className="ct-day-nav">
          <button
            className="ct-day-nav-btn"
            onClick={() => shiftDay(-1)}
            title="Previous day"
            aria-label="Previous day"
          >
            <ChevronLeft size={15} />
          </button>

          <div className="ct-day-nav-label">
            <Calendar size={13} className="ct-day-nav-icon" />
            <span className="ct-day-nav-date">{fmtDay(selectedDay)}</span>
            {isToday && <span className="ct-day-nav-today-badge">Today</span>}
          </div>

          <button
            className={`ct-day-nav-btn ${isToday ? 'ct-day-nav-btn-disabled' : ''}`}
            onClick={() => shiftDay(1)}
            disabled={isToday}
            title="Next day"
            aria-label="Next day"
          >
            <ChevronRight size={15} />
          </button>

          {!isToday && (
            <button
              className="ct-day-nav-today-btn"
              onClick={jumpToToday}
              title="Jump to today"
            >
              Today
            </button>
          )}
        </div>
      </div>

      {/* ── Loading / Error ─────────────────────────────────────────────────── */}
      {isLoading && (
        <div className="ct-loading">
          <div className="ct-spinner" />
          <span>Loading transition data…</span>
        </div>
      )}

      {!isLoading && error && (
        <div className="ct-error">
          <span>⚠️ {error}</span>
          <button className="ct-retry-btn" onClick={fetchData}>Retry</button>
        </div>
      )}

      {!isLoading && !error && transitions.length === 0 && (
        <LprJourneyGhostPreview
          imageSrc={lprJourneyCameraTransitionImg}
          title="What Camera Transitions looks like"
          description="When vehicles pass through multiple cameras, you'll see a map with routes between cameras, transition counts, and travel times. Configure your map above and let traffic flow to see live data."
          primaryLabel="Configure Map"
          onPrimaryClick={onConfigureMap}
          onHelpClick={onShowHelp}
        />
      )}

      {!isLoading && !error && transitions.length > 0 && (
        <>
          {/* ── Summary stats ─────────────────────────────────────────────── */}
          <div className="ct-stats-row">
            <StatCard
              icon={TrendingUp}
              label="Total Transitions"
              value={totalTransitions.toLocaleString()}
              sub={`across ${transitions.length} route${transitions.length !== 1 ? 's' : ''}`}
            />
            <StatCard
              icon={Clock}
              label="Avg Travel Time"
              value={fmtDuration(avgDuration)}
              sub="across all routes"
            />
            <StatCard
              icon={ArrowRight}
              label="Active Cameras"
              value={uniqueCameras}
              sub="involved in transitions"
            />
          </div>

          {/* ── Transition Route Map ───────────────────────────────────────── */}
          <div className="ct-trm-section">
            <div className="ct-trm-header">
              <span className="ct-trm-title">
                <Map size={14} className="ct-trm-title-icon" />
                Transition Route Map
              </span>
              <div className="ct-trm-header-right">
                <span className="ct-trm-subtitle">
                  All routes for {fmtDay(selectedDay)} · hover a route for details
                </span>
                <button
                  className="ct-trm-maximize-btn"
                  onClick={() => setIsMapFullscreen(true)}
                  title="Expand map to full screen"
                  aria-label="Expand map to full screen"
                >
                  <Maximize2 size={13} />
                </button>
              </div>
            </div>
            {/* Inline map — constrained height */}
            <div className="ct-trm-inline-canvas">
              <TransitionRouteMap
                enrichedTransitions={enriched}
                config={config}
                cameras={cameras}
                networkId={networkId}
                isInline
                onRouteClick={({ fromId, toId }) => {
                  const fromRow = enriched.find(r => r.from_camera_id === fromId);
                  const toRow   = enriched.find(r => r.to_camera_id   === toId);
                  if (fromRow) setFilterFrom(fromRow.from_camera_name);
                  if (toRow)   setFilterTo(toRow.to_camera_name);
                }}
                onConfigureMap={onConfigureMap}
                onShowHelp={onShowHelp}
              />
            </div>
          </div>

          {/* ── Fullscreen map modal ────────────────────────────────────────── */}
          {isMapFullscreen && (
            <div
              className="ct-trm-modal-overlay"
              onClick={(e) => { if (e.target === e.currentTarget) setIsMapFullscreen(false); }}
              role="dialog"
              aria-modal="true"
              aria-label="Transition Route Map — full screen"
            >
              <div className="ct-trm-modal">
                {/* Modal header */}
                <div className="ct-trm-modal-header">
                  <span className="ct-trm-title">
                    <Map size={14} className="ct-trm-title-icon" />
                    Transition Route Map
                    {networkName && <span className="ct-title-network">— {networkName}</span>}
                  </span>
                  <div className="ct-trm-modal-header-right">
                    <span className="ct-trm-subtitle">
                      {fmtDay(selectedDay)} · hover a route for details · ESC to close
                    </span>
                    <button
                      className="ct-trm-modal-close-btn"
                      onClick={() => setIsMapFullscreen(false)}
                      title="Close full screen map"
                      aria-label="Close full screen map"
                    >
                      <X size={16} />
                    </button>
                  </div>
                </div>
                {/* Modal map canvas — fills remaining modal height */}
                <div className="ct-trm-modal-canvas">
                  <TransitionRouteMap
                    enrichedTransitions={enriched}
                    config={config}
                    cameras={cameras}
                    networkId={networkId}
                    onRouteClick={({ fromId, toId }) => {
                      const fromRow = enriched.find(r => r.from_camera_id === fromId);
                      const toRow   = enriched.find(r => r.to_camera_id   === toId);
                      if (fromRow) setFilterFrom(fromRow.from_camera_name);
                      if (toRow)   setFilterTo(toRow.to_camera_name);
                      setIsMapFullscreen(false); // close after click-to-filter
                    }}
                    onConfigureMap={onConfigureMap}
                    onShowHelp={onShowHelp}
                  />
                </div>
              </div>
            </div>
          )}

          {/* ── Filter + Table ─────────────────────────────────────────────── */}
          <div className="ct-table-section">
            <div className="ct-table-header">
              <span className="ct-chart-title">
                All Routes
                <span className="ct-table-count">{filtered.length}</span>
              </span>
              <div className="ct-filters">
                <input
                  className="ct-filter-input"
                  placeholder="Filter from camera…"
                  value={filterFrom}
                  onChange={e => setFilterFrom(e.target.value)}
                />
                <input
                  className="ct-filter-input"
                  placeholder="Filter to camera…"
                  value={filterTo}
                  onChange={e => setFilterTo(e.target.value)}
                />
              </div>
            </div>

            {historyError && (
              <div className="ct-history-error">
                ⚠️ Historical baseline unavailable: {historyError}
              </div>
            )}

            <div className="ct-table-wrapper">
              <table className="ct-table">
                <thead>
                  <tr>
                    {/* Anomaly rank column — sortable */}
                    <SortableTh colKey="anomaly">#</SortableTh>
                    <th className="ct-th">From Camera</th>
                    <th className="ct-th ct-th-arrow"></th>
                    <th className="ct-th">To Camera</th>
                    <SortableTh colKey="transitions" className="ct-th-num">Transitions</SortableTh>
                    <SortableTh colKey="duration"    className="ct-th-num">Avg Duration</SortableTh>
                    <SortableTh colKey="history"     className="ct-th-num">
                      History Avg
                      <span className="ct-th-sub">({HISTORY_WINDOW_DAYS}d)</span>
                    </SortableTh>
                    <th className="ct-th ct-th-num">Min / Max</th>
                    <th className="ct-th ct-th-num">
                      History Min / Max
                      <span className="ct-th-sub">({HISTORY_WINDOW_DAYS}d)</span>
                    </th>
                    <th className="ct-th ct-th-num"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPage.map((row, pageIdx) => {
                    const i = (routesPageSafe - 1) * ROUTES_PAGE_SIZE + pageIdx;
                    const isExpanded = expandedRow === i;
                    const pct = transitions.length
                      ? (row.total_transitions / totalTransitions) * 100
                      : 0;
                    const anomalyRank = i + 1;

                    return (
                      <>
                        <tr
                          key={`${row.from_camera_id}-${row.to_camera_id}`}
                          className={`ct-tr ${isExpanded ? 'ct-tr-expanded' : ''}`}
                          onClick={() => setExpandedRow(isExpanded ? null : i)}
                        >
                          {/* Anomaly rank badge */}
                          <td className="ct-td ct-td-rank">
                            {sortBy === 'anomaly' && row.anomaly_score !== Infinity
                              ? <AnomalyBadge rank={anomalyRank} />
                              : <span className="ct-rank-plain">{anomalyRank}</span>}
                          </td>

                          <td className="ct-td">
                            <span className="ct-cam-name">{row.from_camera_name}</span>
                            <span className="ct-cam-id">{row.from_camera_id}</span>
                          </td>
                          <td className="ct-td ct-td-arrow">
                            <ArrowRight size={13} className="ct-row-arrow" />
                          </td>
                          <td className="ct-td">
                            <span className="ct-cam-name">{row.to_camera_name}</span>
                            <span className="ct-cam-id">{row.to_camera_id}</span>
                          </td>

                          {/* Transitions */}
                          <td className="ct-td ct-td-num">
                            <div className="ct-count-cell">
                              <span className="ct-count-val">{row.total_transitions.toLocaleString()}</span>
                              <div className="ct-count-bar-track">
                                <div
                                  className="ct-count-bar-fill"
                                  style={{ width: `${pct}%` }}
                                />
                              </div>
                            </div>
                          </td>

                          {/* Avg Duration + trend arrow */}
                          <td className="ct-td ct-td-num ct-dur">
                            <span className="ct-dur-cell">
                              {fmtDuration(row.avg_duration_seconds)}
                              {!historyLoading && (
                                <TrendArrow
                                  todayAvg={row.avg_duration_seconds}
                                  historyAvg={row.history_avg}
                                />
                              )}
                              {historyLoading && row.avg_duration_seconds !== null && (
                                <HistorySkeleton />
                              )}
                            </span>
                          </td>

                          {/* History Avg Duration */}
                          <td className="ct-td ct-td-num ct-history-dur">
                            {historyLoading
                              ? <HistorySkeleton />
                              : row.history_avg !== null
                                ? fmtDuration(row.history_avg)
                                : <span className="ct-no-history">—</span>}
                          </td>

                          {/* Min / Max */}
                          <td className="ct-td ct-td-num ct-dur-range">
                            {row.min_duration_seconds !== null
                              ? `${fmtDuration(row.min_duration_seconds)} / ${fmtDuration(row.max_duration_seconds)}`
                              : '—'}
                          </td>

                          {/* History Min / Max */}
                          <td className="ct-td ct-td-num ct-history-range">
                            {historyLoading
                              ? <HistorySkeleton />
                              : row.history_min !== null
                                ? `${fmtDuration(row.history_min)} / ${fmtDuration(row.history_max)}`
                                : <span className="ct-no-history">—</span>}
                          </td>

                          {/* Expand chevron */}
                          <td className="ct-td ct-td-num">
                            {isExpanded
                              ? <ChevronUp size={13} className="ct-chevron" />
                              : <ChevronDown size={13} className="ct-chevron" />}
                          </td>
                        </tr>

                        {isExpanded && (
                          <tr key={`${row.from_camera_id}-${row.to_camera_id}-exp`} className="ct-tr-detail">
                            <td colSpan={10}>
                              <div className="ct-detail-panel">
                                <div className="ct-detail-body">
                                  {/* ── Mini map ─────────────────────────────── */}
                                  <div className="ct-detail-map-col">
                                    <p className="ct-detail-map-title">
                                      <MapPin size={11} />
                                      Route Map
                                    </p>
                                    <RouteMap
                                      fromCameraId={row.from_camera_id}
                                      toCameraId={row.to_camera_id}
                                      fromCameraName={row.from_camera_name}
                                      toCameraName={row.to_camera_name}
                                      config={config}
                                      networkId={networkId}
                                    />
                                  </div>

                                  {/* ── Stats grid ───────────────────────────── */}
                                  <div className="ct-detail-stats-col">
                                    <div className="ct-detail-grid">
                                      <div className="ct-detail-item">
                                        <span className="ct-detail-label">Route</span>
                                        <span className="ct-detail-value">
                                          {row.from_camera_name} → {row.to_camera_name}
                                        </span>
                                      </div>
                                      <div className="ct-detail-item">
                                        <span className="ct-detail-label">Camera IDs</span>
                                        <span className="ct-detail-value ct-mono">
                                          {row.from_camera_id} → {row.to_camera_id}
                                        </span>
                                      </div>
                                      <div className="ct-detail-item">
                                        <span className="ct-detail-label">Share of traffic</span>
                                        <span className="ct-detail-value">{pct.toFixed(1)}%</span>
                                      </div>
                                      <div className="ct-detail-item">
                                        <span className="ct-detail-label">Anomaly score</span>
                                        <span className="ct-detail-value">
                                          {row.anomaly_score === Infinity
                                            ? 'N/A (no history)'
                                            : `${row.anomaly_score.toFixed(1)}% deviation`}
                                        </span>
                                      </div>
                                      <div className="ct-detail-item">
                                        <span className="ct-detail-label">History avg ({HISTORY_WINDOW_DAYS}d)</span>
                                        <span className="ct-detail-value">
                                          {historyLoading
                                            ? 'Loading…'
                                            : row.history_avg !== null
                                              ? fmtDuration(row.history_avg)
                                              : 'N/A'}
                                        </span>
                                      </div>
                                      <div className="ct-detail-item">
                                        <span className="ct-detail-label">History min / max ({HISTORY_WINDOW_DAYS}d)</span>
                                        <span className="ct-detail-value">
                                          {historyLoading
                                            ? 'Loading…'
                                            : row.history_min !== null
                                              ? `${fmtDuration(row.history_min)} / ${fmtDuration(row.history_max)}`
                                              : 'N/A'}
                                        </span>
                                      </div>
                                      <div className="ct-detail-item">
                                        <span className="ct-detail-label">Last calculated</span>
                                        <span className="ct-detail-value">
                                          {row.last_calculated
                                            ? row.last_calculated.replace('T', ' ')
                                            : '—'}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </>
                    );
                  })}
                </tbody>
              </table>

              {filtered.length === 0 && (
                <div className="ct-table-empty">No routes match the current filters.</div>
              )}

              {routesTotalPages > 1 && (
                <div className="ct-pagination">
                  <button
                    className="ct-page-btn"
                    onClick={() => { setRoutesPage(1); setExpandedRow(null); }}
                    disabled={routesPageSafe === 1}
                  >«</button>
                  <button
                    className="ct-page-btn"
                    onClick={() => { setRoutesPage(p => Math.max(1, p - 1)); setExpandedRow(null); }}
                    disabled={routesPageSafe === 1}
                  >‹</button>
                  <span className="ct-page-info">
                    Page {routesPageSafe} of {routesTotalPages}
                    <span className="ct-page-count">
                      &nbsp;({filtered.length} routes)
                    </span>
                  </span>
                  <button
                    className="ct-page-btn"
                    onClick={() => { setRoutesPage(p => Math.min(routesTotalPages, p + 1)); setExpandedRow(null); }}
                    disabled={routesPageSafe === routesTotalPages}
                  >›</button>
                  <button
                    className="ct-page-btn"
                    onClick={() => { setRoutesPage(routesTotalPages); setExpandedRow(null); }}
                    disabled={routesPageSafe === routesTotalPages}
                  >»</button>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CameraTransitions;
